/* eslint-disable @typescript-eslint/no-explicit-any */
import { useThemeMode } from "components/ThemeSwitcher/useThemeMode";
import React, { useEffect, useMemo, useRef, useState, createContext } from "react";
import { Outlet, useNavigate, NavLink, useLocation } from "react-router-dom";
import Items from "./SideBarItems";
import { useTranslation } from "react-i18next";
import { storageService } from "services/storageService";
import apiService from "services/apiService";
import { useAppContext } from "contexts/useAppContext";
import Image from "assets/avatar.png";
// import { IoLogOut } from "react-icons/io5";
import { LogOut, KeyRound, ArrowUpCircle, Sun, Moon } from "lucide-react";
import GenericModal from "components/Modal";
// Note: version and build number are sourced from buildInfo, not package.json directly,
// so that Jenkins-injected REACT_APP_* values are reflected after each deployment.
import { APP_VERSION, BUILD_NUMBER } from "util/buildInfo";
import { Dropdown } from "flowbite-react";
import { CgProfile } from "react-icons/cg";
import { useMediaQuery } from "react-responsive";
import ChatComponent from "./chat/ChatComponent";
import OrgSwitcher, { isOrgSwitcherVisible } from "../../layouts/Orgswitcher";
// Cast Draggable to accept partial props — react-draggable 4.7 declares `props`
// as the full non-partial type, causing TS2740 even though all props have defaults.
import DraggableLib, { DraggableProps } from "react-draggable";
const Draggable = DraggableLib as React.ComponentType<Partial<DraggableProps>>;
import ChangePassword from "./ResetPassword";
import { hasModuleAccess, parseJwtPayload } from "util/moduleAccess";
import { isActiveStatus, isOnboardingStatus } from "util/userStatus";
import { NotificationBadgeContext } from "contexts/notificationBadgeContext";
import { FaAngleDown } from "react-icons/fa";
import { Banknote } from 'lucide-react';
import PlanNotificationBanner from "./PlanNotificationBanner"; // Adjust the import path if placed elsewhere
// Single org-scoped source of truth for the plan (banner + Upgrade Plan menu item)
import useSubscriptionStatus, { clearOrgScopedPlanSession } from "./useSubscriptionStatus";
import LanguageMenu from "./LanguageMenu";
import { MENU_DROPDOWN_THEME } from "./menuItemStyles";
import useNotificationBadge from "./useNotificationBadge";
import NotificationBell, { NotificationDot } from "./NotificationBell";
import { NotificationProvider } from "pages/firebase/NotificationProvider";
import { getStoredDeviceToken, clearStoredDeviceToken } from "pages/firebase/notificationApi";
import { getFlaggedSidebarUrls, getAutoReadTypesForSidebarUrl } from "services/Notification/notificationInterface";
import notificationManager from "services/Notification/notificationManager";
export const BannerContext = createContext<{ isBannerVisible: boolean }>({ isBannerVisible: false });

export default function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setUserRole, userRole, user, setEmail } = useAppContext();
  const [logout, setLogout] = useState(false);
  const [isUserActive, setIsUserActive] = useState<boolean>(true);
  // ONBOARDING users may use the app, but only the Profile area (Profile +
  // Documents sub-tabs). Tracked separately from isUserActive so the app still
  // renders for them while the rest of the navigation is hidden.
  const [isOnboarding, setIsOnboarding] = useState<boolean>(false);
  // Whether the real account status is known yet. The Sidebar mounts once per app
  // load, so we always wait for the authoritative me-call before rendering the
  // menu — this way an onboarding user never flashes the full navigation and then
  // collapses to Profile. Until resolved the menu shows a skeleton.
  const [statusResolved, setStatusResolved] = useState<boolean>(false);
  const [isOpenmSidebar, setIsOpenSidebar] = useState(false);
  const [isChatProfileOpen, setIsChatProfileOpen] = useState(false);
  const [chatButtonPosition, setChatButtonPosition] = useState(() => ({
    x: window.innerWidth - 70,   // 64px button + 20px margin from right
    y: window.innerHeight - 120,  // 64px button + 20px margin from bottom
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [isManualLogin, setIsManualLogin] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [accessTokenVersion, setAccessTokenVersion] = useState(0);
  const [isSalaryDrawerOpen, setIsSalaryDrawerOpen] = useState(false);
  const { isDarkMode, toggleTheme } = useThemeMode();
  const [isBannerActive, setIsBannerActive] = useState(true);
  // Server-side build version fetched from api/user/me response
  const [serverBuildVersion, setServerBuildVersion] = useState<string | null>(null);
  console.log("User. details : ", user)

  useEffect(() => {
    if (user && user.userStatus) {
      // ACTIVE and ONBOARDING users can both enter the app (so the Profile area
      // renders); only ONBOARDING is additionally restricted. Any other status
      // (e.g. INACTIVE) keeps the user blocked behind the "contact admin" modal.
      if (isActiveStatus(user.userStatus)) {
        setIsUserActive(true);
        setIsOnboarding(false);
      } else if (isOnboardingStatus(user.userStatus)) {
        setIsUserActive(true);
        setIsOnboarding(true);
      } else {
        setIsUserActive(false);
        setIsOnboarding(false);
      }
      setStatusResolved(true);
    }
  }, [user?.userStatus]);

  const isMobile = useMediaQuery({ maxWidth: 639 });

  const handleLogout = async () => {
    try {
      // Note: pass the stored FCM device token (FID) in the logout body so the
      // server can remove just this browser's row from the DB and stop pushing
      // to a device the user has signed out of. Read BEFORE clearing storage.
      const fcmToken = getStoredDeviceToken();
      await apiService.post("/api/auth/logout", fcmToken ? { fcmToken } : {});
      clearStoredDeviceToken();
      localStorage.clear();
      sessionStorage.clear();
      // sessionStorage.removeItem("refreshToken");
    } catch (error) {
      console.log("Logout API failed:", error);
    } finally {
      storageService.removeItem("accessToken");

      // Revert theme to light mode on logout
      window.document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");

      navigate("/login");
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setChatButtonPosition({
        x: window.innerWidth - 84,
        y: window.innerHeight - 84,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChatIconClick = () => {
    if (isDragging) return;
    setIsChatProfileOpen(true);
  };

  const handleDrag = (e: any, data: any) => {
    setIsDragging(true);
    setChatButtonPosition({ x: data.x, y: data.y });
  };

  const handleStop = () => {
    // Small timeout prevents click trigger after drag
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  const checkUserManualLogin = async () => {
    await apiService.get("api/has-password")
      .then((res) => {
        if (res.data?.hasPassword && res.data?.provider === "local") {
          setIsManualLogin(true);
        }
      })
      .catch((err) => {
        console.log("Error checking has-password API", err);
      });
  }

  const fetchCurrentUser = () => {
    apiService
      .get("api/user/me")
      .then((res) => {
        const Data = res.data.user;
        // Capture server build version from the api/user/me response
        if (res.data.buildVersion) {
          setServerBuildVersion(res.data.buildVersion);
        }
        const status = res.data.user?.userStatus;
        const active = isActiveStatus(status);
        const onboarding = isOnboardingStatus(status);
        // Load the user into context for ACTIVE and ONBOARDING accounts so the
        // app can render (ONBOARDING is restricted to the Profile area by the
        // menu/route guards below). Everything else stays blocked.
        //
        // All status flags are set together here (rather than via a separate
        // effect) so the first post-resolve render is already consistent — an
        // onboarding user never briefly shows the full menu.
        if (active || onboarding) {
          storageService.setItem("user", JSON.stringify(Data));
          setUser(res.data.user);
          setUserRole(res.data.user.role);
          setEmail(res.data.user.email);
          setIsUserActive(true);
          setIsOnboarding(onboarding);
        } else {
          setIsUserActive(false);
          setIsOnboarding(false);
        }
        setStatusResolved(true);
      })
      .catch((err) => {
        console.log("err :>> ", err);
        // Don't trap the UI in the loading state if the status call fails —
        // fall back to the role-based menu.
        setStatusResolved(true);
      });
  };

  useEffect(() => {
    checkUserManualLogin();
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const handleAuthTokenUpdated = () => {
      setAccessTokenVersion((prev) => prev + 1);
      fetchCurrentUser();
      checkUserManualLogin();
    };
    const handleRefreshUserData = () => {
      fetchCurrentUser();
    };

    window.addEventListener("auth-token-updated", handleAuthTokenUpdated as EventListener);
    window.addEventListener("refresh-user-data", handleRefreshUserData as EventListener);
    return () => {
      window.removeEventListener("auth-token-updated", handleAuthTokenUpdated as EventListener);
      window.removeEventListener("refresh-user-data", handleRefreshUserData as EventListener);
    };
  }, []);

  /**
   * Cross-tab session sync.
   *
   * NOTE: `localStorage` is shared across every tab of the same origin, but the
   * `user` shown in the header lives in per-tab in-memory context state. The
   * custom events above (auth-token-updated / refresh-user-data) are dispatched
   * with `window.dispatchEvent`, which only fires in the SAME tab — so a login
   * in another tab never reaches this one. The browser's `storage` event is the
   * only signal that fires in the OTHER tabs when localStorage changes, so we
   * listen for it here.
   *
   * Because the shared token has already been overwritten by the other tab, this
   * tab's API calls now silently use the new user's token while the header still
   * shows the old user — an identity mismatch. The safe resolution is to
   * re-initialise the whole app against the new session: reload on a token
   * change, or redirect to /login when the token was cleared (logout).
   */
  const activeTokenRef = useRef<string | null>(storageService.getItem("accessToken"));
  useEffect(() => {
    const handleAuthStorageChange = (event: StorageEvent) => {
      // Only react to auth changes. `localStorage.clear()` fires with key === null.
      const isAuthKey = event.key === "accessToken" || event.key === null;
      if (!isAuthKey) return;

      const currentToken = storageService.getItem("accessToken");
      // Ignore events that don't actually change the token this tab is using.
      if (currentToken === activeTokenRef.current) return;

      activeTokenRef.current = currentToken;

      // Logged out / cleared in another tab → follow it to the login screen.
      if (!currentToken) {
        navigate("/login");
        return;
      }

      // A different user signed in via another tab. Reload so the entire app
      // (header profile, role, menus, data) re-initialises against the new,
      // now-shared session and stays consistent.
      window.location.reload();
    };

    window.addEventListener("storage", handleAuthStorageChange);
    return () => window.removeEventListener("storage", handleAuthStorageChange);
  }, [navigate]);

  useEffect(() => {
    const handleUserProfileUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ imageUrl?: string }>;
      const nextImageUrl = customEvent?.detail?.imageUrl;
      if (!nextImageUrl) return;

      setUser((prev: any) => {
        const updated = {
          ...(prev || {}),
          imageUrl: nextImageUrl,
        };
        storageService.setItem("user", JSON.stringify(updated));
        return updated;
      });
    };

    window.addEventListener("user-profile-updated", handleUserProfileUpdated as EventListener);
    return () => {
      window.removeEventListener("user-profile-updated", handleUserProfileUpdated as EventListener);
    };
  }, [setUser]);

  const token = storageService.getItem("accessToken");
  const payload = parseJwtPayload(token);

  const jwtRole = (payload?.role || "").toString().trim().toUpperCase();
  const isPlanAdmin = jwtRole === "ROLE_ADMIN";

  /**
   * Live plan for the organisation in session, shared with the plan banner.
   * Refetched on every organisation switch (see useSubscriptionStatus).
   */
  const { status: subscriptionStatus, activeSubdomain } = useSubscriptionStatus({
    enabled: isPlanAdmin,
  });

  /**
   * Upgrade Plan entry in the account menu.
   *
   * NOTE: the plan is taken from the subscription API, with the JWT claims as
   * the only fallback. It used to read sessionStorage first, which is where the
   * Upgrade Plan / Pending Invoice hand-off writes the plan it was launched
   * with — that value survives an organisation switch, so returning from
   * Pending Invoice and switching to an ENTERPRISE organisation kept showing
   * "Upgrade Plan" for a plan that belonged to the previous organisation.
   */
  const showOrgSwitcher = isOrgSwitcherVisible();
  console.log(showOrgSwitcher)
  const hasPlanBanner = isPlanAdmin && subscriptionStatus?.showBanner === true;
  const isBannerVisible = hasPlanBanner && isBannerActive;

  const currentPlan = (
    subscriptionStatus?.plan ||
    subscriptionStatus?.planType ||
    payload?.plan ||
    (payload as any)?.planType ||
    ""
  )
    .toString()
    .trim()
    .toUpperCase();

  // Only these two can be upgraded from here; ENTERPRISE and anything else hide it
  const canUpgradePlan = currentPlan === "BASIC" || currentPlan === "PREMIUM";

  /**
   * Switching organisation must not leave the previous organisation's plan
   * hand-off behind for PlanSelection / PendingInvoices to pick up.
   */
  useEffect(() => {
    const handleOrgChanged = () => clearOrgScopedPlanSession();

    window.addEventListener("auth-token-updated", handleOrgChanged as EventListener);
    return () => {
      window.removeEventListener("auth-token-updated", handleOrgChanged as EventListener);
    };
  }, []);

  /**
   * Mirrors the plan banner's hand-off: PlanSelection resolves the tenant, the
   * current plan and the user id from navigation state with a sessionStorage
   * fallback, so both are populated here before navigating.
   */
  const handleUpgradePlanClick = () => {
    const storedUser = storageService.getItem("user");
    const userId = storedUser ? JSON.parse(storedUser)?.id : null;
    const schemaName = (payload as any)?.tenant || "";

    if (userId) sessionStorage.setItem("userId", userId.toString());
    if (currentPlan) sessionStorage.setItem("plan", currentPlan);
    if (schemaName) sessionStorage.setItem("schemaName", schemaName.toString());

    navigate("/plan-selection", {
      state: { id: userId, plan: currentPlan, schemaName },
    });
  };

  const menuItems = useMemo(
    () => {
      // Withhold the menu until we know the account status, so an onboarding user
      // never flashes the full navigation before it collapses to Profile.
      if (!statusResolved) {
        return [];
      }
      // ONBOARDING users only ever see the Profile item. We pick it straight from
      // the master list (not the module-filtered one) so it always appears even if
      // the account's module grants are still being provisioned.
      if (isOnboarding) {
        return Items.filter((item) => item.url === "profile");
      }
      return Items.filter(
        (item) =>
          item.roles.includes(userRole) && hasModuleAccess(payload, item.moduleKey, userRole),
      );
    },
    [accessTokenVersion, payload, userRole, isOnboarding, statusResolved],
  );

  // Keep ONBOARDING users confined to the Profile area even if they try to reach
  // another route via a direct URL or a stale link.
  useEffect(() => {
    if (isOnboarding && location.pathname !== "/admin/profile") {
      navigate("/admin/profile", { replace: true });
    }
  }, [isOnboarding, location.pathname, navigate]);

  // Notification badge summary (shared by the header bell + sidebar tab dots).
  const {
    data: notificationBadge,
    loading: notificationsLoading,
    refresh: refreshNotifications,
    markAllRead: markAllNotificationsRead,
    // Onboarding users are included: their Profile/Document notifications are the
    // whole point for them, and those only ever navigate within the Profile area.
  } = useNotificationBadge({ enabled: isUserActive });

  // Sidebar routes that currently have pending notifications → show a red dot.
  const flaggedSidebarUrls = useMemo(
    () => getFlaggedSidebarUrls(notificationBadge),
    [notificationBadge],
  );

  /**
   * Auto-read informational notifications when the user opens the related tab.
   *
   * When the current route matches a tab that has non-action notifications
   * (actionRequired === false), mark each of those types read via
   * PATCH /api/notifications/read?type=TYPE, then refresh so the tab's red dot
   * clears. Action-required notifications are left untouched — they need the user
   * to actually resolve them, not just visit the tab.
   *
   * `autoReadRef` tracks "already handled" url:type keys so a badge refresh (which
   * re-runs this effect) can't loop; a key is re-armed once its type disappears,
   * so a later re-occurrence is read again.
   */
  const autoReadRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!isUserActive) return;
    // Routes live under /admin/<url>; grab the first segment as the tab url.
    const currentUrl = location.pathname.match(/\/admin\/([^/]+)/)?.[1];
    if (!currentUrl) return;

    const types = getAutoReadTypesForSidebarUrl(notificationBadge, currentUrl);

    // Re-arm keys for this tab whose type is no longer present, so it can be
    // auto-read again if the same notification type arrives later.
    const presentKeys = new Set(types.map((t) => `${currentUrl}:${t}`));
    autoReadRef.current.forEach((key) => {
      if (key.startsWith(`${currentUrl}:`) && !presentKeys.has(key)) {
        autoReadRef.current.delete(key);
      }
    });

    const toRead = types.filter((t) => !autoReadRef.current.has(`${currentUrl}:${t}`));
    if (toRead.length === 0) return;

    toRead.forEach((t) => autoReadRef.current.add(`${currentUrl}:${t}`));

    void (async () => {
      try {
        await Promise.all(toRead.map((type) => notificationManager.readByType(type)));
        refreshNotifications();
      } catch (err) {
        console.error("Failed to auto-read notifications for tab:", currentUrl, err);
        // Allow a retry on the next visit / badge refresh.
        toRead.forEach((t) => autoReadRef.current.delete(`${currentUrl}:${t}`));
      }
    })();
  }, [location.pathname, notificationBadge, isUserActive, refreshNotifications]);

  const salaryDrawerItems = [
    { label: "Salary Structure", url: "salaryStructure", icon: Banknote, color: "text-black dark:text-white" },
    { label: "Salary Heads", url: "salary", icon: Banknote, color: "text-black dark:text-white" },
    { label: "Salary", url: "salary-list", icon: Banknote, color: "text-black dark:text-white" },
    { label: "Salary History", url: "salary-history", icon: Banknote, color: "text-black dark:text-white" },
    { label: "Generate Payroll", url: "generate-payroll", icon: Banknote, color: "text-black dark:text-white" },
  ];

  const salaryDrawerUrls = salaryDrawerItems.map((item) => item.url);
  const isSalaryRouteActive = salaryDrawerUrls.some((url) =>
    location.pathname.includes(`/admin/${url}`),
  );

  const salaryStructureIndex =
    menuItems?.findIndex((item: { url: string }) => item.url === "salaryStructure") ?? -1;

  const beforeDrawerItems =
    menuItems?.filter(
      (item: { url: string }, index: number) =>
        (salaryStructureIndex !== -1 ? index < salaryStructureIndex : true) && !salaryDrawerUrls.includes(item.url),
    ) || [];

  const afterDrawerItems =
    menuItems?.filter(
      (item: { url: string }, index: number) =>
        (salaryStructureIndex !== -1 ? index > salaryStructureIndex : false) && !salaryDrawerUrls.includes(item.url),
    ) || [];

  // Helper to determine if a menu item should be active
  const activeMenuLabel = useMemo(() => {
    const matchedSalaryItem = salaryDrawerItems.find((item) =>
      location.pathname.includes(`/admin/${item.url}`),
    );
    if (matchedSalaryItem) return matchedSalaryItem.label;

    const matchedItem = menuItems?.find((item: { url: string }) =>
      location.pathname.includes(`/admin/${item.url}`),
    );
    return matchedItem?.label ?? "";
  }, [location.pathname, menuItems]);

  const checkIsActive = (isActive: boolean, url: string) => {
    if (isActive) return true;

    // If we are on the chat page, manually force the "projects" menu item to be active
    const isChatRoute = location.pathname.startsWith("/admin/chat");
    const isProjectMenu = url.toLowerCase().includes("project");

    if (isChatRoute && isProjectMenu) {
      return true;
    }

    return false;
  };

  const accountMenu = (
    <Dropdown
      label=""
      // Account menu now sits at the sidebar footer, so open upward from the
      // avatar's left edge to keep the menu on-screen.
      placement="top-start"
      theme={MENU_DROPDOWN_THEME}
      renderTrigger={() => (
        <img
          className="w-8 h-8 rounded-full bg-white-100 cursor-pointer"
          src={user ? user.imageUrl || Image : Image}
          alt="user photo"
          loading="lazy"
        />
      )}
    >
      {/* Header per Figma: horizontal, rounded avatar (image or initials
          fallback) + name/email, 12px gap, 10px bottom padding, 1px #F1F5F9
          bottom border. */}
      {/** Here removed border to match figma design */}
      <Dropdown.Header className="!p-0">
        <div className="flex items-center gap-3  dark:border-gray-700 pb-2.5">
          {user?.imageUrl ? (
            <img
              src={user.imageUrl}
              alt={user.displayName || "User"}
              className="h-10 w-10 shrink-0 rounded-full object-cover"
              loading="lazy"
            />
          ) : (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-sm font-bold uppercase text-white">
              {user?.displayName?.trim()?.charAt(0) || "?"}
            </span>
          )}
          <div className="min-w-0">
            <p className="truncate text-base font-bold capitalize text-gray-900 dark:text-white">
              {user ? user.displayName : "—"}
            </p>
            <p className="truncate text-sm font-normal text-gray-500 dark:text-gray-400">
              {user && user.email}
            </p>
          </div>
        </div>
      </Dropdown.Header>

      <Dropdown.Item
        onClick={() => navigate("profile")}
        icon={CgProfile}
      >
        Profile
      </Dropdown.Item>
      {/** Commented <Dropdown.Divider /> to match figma design */}
      {/* <Dropdown.Divider /> */}

      {/* 3. Replaced the custom button with Dropdown.Item for full-width hover effect */}
      <Dropdown.Item
        onClick={toggleTheme}
        // Passing an inline component keeps your custom colors perfectly aligned with the other icons
        icon={() =>
          isDarkMode ? (
            <Sun className="mr-3 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
          ) : (
            <Moon className="mr-3 h-5 w-5 shrink-0 text-gray-800" aria-hidden="true" />
          )
        }
      >
        {t("Theme")}
      </Dropdown.Item>

      {/* <Dropdown.Divider /> */}

      {/* Note: Ensure LanguageMenu & OrgSwitcher components also use <Dropdown.Item> internally to avoid similar hover issues */}
      <LanguageMenu />

      {/* {showOrgSwitcher && <Dropdown.Divider />} */}

      <OrgSwitcher variant="menu" />

      {/* <Dropdown.Divider /> */}

      {isManualLogin && (
        <Dropdown.Item
          onClick={() => setIsResettingPassword(true)}
          icon={KeyRound}
        >
          Change Password
        </Dropdown.Item>
      )}

      {/* {isManualLogin && <Dropdown.Divider />} */}

      {/* Shown just above Log out for upgradable plans only */}
      {canUpgradePlan && (
        <Dropdown.Item
          onClick={handleUpgradePlanClick}
          icon={ArrowUpCircle}
        >
          Upgrade Plan
        </Dropdown.Item>
      )}

      {/* {canUpgradePlan && <Dropdown.Divider />} */}

      <Dropdown.Divider />

      <Dropdown.Item
        onClick={() => setLogout(true)}
        icon={LogOut}
        className="!text-red-600 hover:!bg-red-50 hover:!text-red-700 dark:!text-red-400 dark:hover:!bg-red-900/20 dark:hover:!text-red-300"
      >
        Log out
      </Dropdown.Item>

      {/* <Dropdown.Divider /> */}

      {/* Build versions — client (web) + server, moved into the account menu. */}
      <div className="px-4 py-2.5 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">
            Web
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-mono font-medium text-gray-600 dark:text-gray-200">
              v{APP_VERSION}
            </span>
            <span className="inline-flex items-center rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 text-[11px] font-mono font-semibold text-gray-700 dark:border-gray-500 dark:bg-gray-600 dark:text-gray-100">
              #{BUILD_NUMBER}
            </span>
          </div>
        </div>
        {serverBuildVersion && (
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">
              Server
            </span>
            <span className="inline-flex items-center rounded border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-[11px] font-mono font-semibold text-blue-700 dark:border-blue-600 dark:bg-blue-900/50 dark:text-blue-200">
              v{serverBuildVersion}
            </span>
          </div>
        )}
      </div>
    </Dropdown>
  );

  const shellClass = isMobile ? "" : "flex h-screen overflow-hidden";
  const sidebarColumnClass = isMobile
    ? ""
    : "w-fit max-w-64 shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-700";
  const navClass = isMobile
    ? "fixed top-0 left-0 z-50 w-full bg-white dark:bg-gray-800"
    : "bg-white dark:bg-gray-800";
  const asideClass = isMobile
    ? "fixed top-0 left-0 z-40 w-64 h-screen pt-16 transition-transform -translate-x-full bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700"
    : "flex-1 min-h-0 bg-white dark:bg-gray-800";
  const navListClass = isMobile
    ? "flex-1 min-h-0 overflow-y-auto space-y-2 font-medium pr-1"
    : "flex-1 min-h-0 overflow-y-auto space-y-2 font-medium pr-1 min-w-max";
  const pageColumnClass = isMobile
    ? "bg-white dark:bg-gray-800 mt-14 h-[calc(100vh-56px)] flex flex-col"
    : "bg-white dark:bg-gray-800 flex-1 min-w-0 flex flex-col";

  return (
    <>
      {/* Firebase Cloud Messaging bootstrap — registers the device token and
          refreshes the notification bell on push (for authenticated users). */}
      <NotificationProvider enabled={isUserActive} />

      {logout && (
        <GenericModal
          isOpen={logout}
          onClose={() => setLogout(false)}
          header="Logout"
        >
          <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
            <div className="p-4 md:p-5 text-center">
              <svg
                className="mx-auto mb-4 text-gray-400 w-12 h-12 dark:text-gray-200"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
              <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                Are you sure you want to log out?
              </h3>
              <button
                data-modal-hide="popup-modal"
                type="button"
                className="text-white bg-red-600 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center"
                onClick={handleLogout}
              >
                Yes, Im sure
              </button>
              <button
                data-modal-hide="popup-modal"
                type="button"
                className="py-2.5 px-5 ms-3 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
                onClick={() => setLogout(false)}
              >
                No, cancel
              </button>
            </div>
          </div>
        </GenericModal>
      )}
      <div className={shellClass}>
        <div className={sidebarColumnClass}>
          <nav className={navClass}>
            {/* Header icon must start on the same vertical line as the sidebar
                tab icons below. Those sit at a CONSTANT 1.5rem (aside pl-3 = 0.75rem
                + each NavLink p-3 = 0.75rem) at every width, so the header's left
                padding is an unconditional pl-6 (1.5rem) — no breakpoint, so they
                can never drift apart. Right padding stays responsive. */}
            <div className="py-2 pl-6 pr-3 lg:pr-5">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {/* Added pr-2 to push the Liriworks icon and text slightly more to the right */}
                  {/* <span className="flex items-center shrink-0 pr-2">{accountMenu}</span> */}
                  <button
                    onClick={() => setIsOpenSidebar((prev) => !prev)}
                    data-drawer-target="logo-sidebar"
                    data-drawer-toggle="logo-sidebar"
                    aria-controls="logo-sidebar"
                    type="button"
                    className="inline-flex items-center p-2 text-sm text-gray-500 rounded-lg sm:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
                  >
                    <span className="sr-only">Open sidebar</span>
                    <svg
                      className="w-6 h-6"
                      aria-hidden="true"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        clipRule="evenodd"
                        fillRule="evenodd"
                        d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                      ></path>
                    </svg>
                  </button>
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0">
                    <svg
                      viewBox="0 0 64 64"
                      className="w-6 h-6"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M10 8c0-3.3 2.7-6 6-6h8v44h18c3.3 0 6 2.7 6 6v6H16c-3.3 0-6-2.7-6-6V8z"
                        fill="#2563EB"
                      />
                      <rect x="28" y="12" width="14" height="14" rx="4" fill="#2563EB" />
                      <rect x="44" y="12" width="14" height="14" rx="4" fill="#2563EB" />
                      <rect x="28" y="28" width="14" height="14" rx="4" fill="#2563EB" />
                      <path d="M44 28h8l6 6v8h-8a6 6 0 0 1-6-6v-8z" fill="#2563EB" />
                    </svg>
                  </div>

                  <span className="text-base font-semibold sm:text-lg whitespace-nowrap dark:text-white antialiased">
                    {t("header")}
                  </span>

                  {activeMenuLabel && (
                    <>
                      <span
                        aria-hidden="true"
                        className="hidden sm:inline-block h-5 w-px bg-slate-200 dark:bg-slate-700 shrink-0"
                      />
                      <span className="hidden sm:inline-block min-w-0 text-sm font-medium text-slate-600 dark:text-slate-300 truncate max-w-[220px]">
                        {activeMenuLabel}
                      </span>
                    </>
                  )}
                </div>

                {/* Notification bell — pinned to the right of the header. */}
                <div className="ml-auto shrink-0">
                  <NotificationBell
                    data={notificationBadge}
                    loading={notificationsLoading}
                    onRefresh={refreshNotifications}
                    onReadAll={markAllNotificationsRead}
                  />
                </div>
              </div>
            </div>
          </nav>

          <aside
            id="logo-sidebar"
            className={asideClass}
            aria-label="Sidebar"
          >
            <div className="h-full flex flex-col pl-3 pr-1 pb-4 bg-white dark:bg-gray-800">
              <ul className={navListClass}>

                {/* While the account status is being resolved, show a skeleton
                    instead of a role-default menu that would otherwise flash and
                    then collapse (e.g. for onboarding users). */}
                {!statusResolved &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <li
                      key={`menu-skeleton-${i}`}
                      className="flex items-center gap-3 p-3 animate-pulse"
                      aria-hidden="true"
                    >
                      <span className="h-6 w-6 rounded-md bg-slate-200 dark:bg-slate-700 shrink-0" />
                      <span className="h-3 flex-1 rounded bg-slate-200 dark:bg-slate-700" />
                    </li>
                  ))}

                {/* DESKTOP: Before Drawer Items */}
                {beforeDrawerItems.map((res: any) => (
                  <li key={res.label}>
                    <NavLink
                      to={`/admin/${res.url}`}
                      className={({ isActive }: any) => {
                        const isCurrentlyActive = checkIsActive(isActive, res.url);

                        return `flex items-center p-3 text-base font-medium rounded-xl transition-all duration-200 group ${isCurrentlyActive
                          ? "text-blue-600 bg-blue-50/80 dark:text-blue-400 dark:bg-blue-900/30"
                          : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/50"
                          }`;
                      }}
                    >
                      <res.icon
                        className="w-6 h-6 shrink-0 transition duration-75"
                        aria-hidden="true"
                      />
                      <span className="ms-3 tracking-wide" style={{ fontVariantLigatures: "none" }}>{res.label}</span>
                    </NavLink>
                  </li>
                ))}

                {salaryStructureIndex !== -1 && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setIsSalaryDrawerOpen((prev) => !prev)}
                      className={`flex items-center w-full p-3 text-base font-medium rounded-xl transition-all duration-200 group ${isSalaryRouteActive
                        ? "text-blue-600 bg-blue-50/80 dark:text-blue-400 dark:bg-blue-900/30"
                        : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/50"
                        }`}
                    >
                      <Banknote className="w-6 h-6 shrink-0 transition duration-75" aria-hidden="true" />
                      <span className="ms-3 tracking-wide" style={{ fontVariantLigatures: "none" }}>Salary</span>
                      <span className="ml-auto">
                        <FaAngleDown
                          className={`h-4 w-4 transition-transform duration-300 ${isSalaryDrawerOpen ? "rotate-180" : "rotate-0"
                            }`}
                        />
                      </span>
                    </button>
                    <ul
                      className={`pl-8 space-y-1 overflow-hidden transition-all duration-300 ease-in-out ${isSalaryDrawerOpen
                        ? "max-h-[520px] opacity-100 mt-1"
                        : "max-h-0 opacity-0 mt-0 pointer-events-none"
                        }`}
                    >
                      {salaryDrawerItems.map((menuItem) => (
                        <li key={menuItem.url}>
                          <NavLink
                            to={`/admin/${menuItem.url}`}
                            className={({ isActive }: { isActive: boolean }) =>
                              `flex items-center gap-2.5 p-2.5 rounded-lg text-sm font-medium transition-all ${isActive
                                ? "text-blue-600 bg-blue-50/70 dark:text-blue-400 dark:bg-blue-900/30"
                                : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/50"
                              }`
                            }
                          >
                            <menuItem.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                            <span className="truncate">{menuItem.label}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </li>
                )}

                {/* DESKTOP: After Drawer Items */}
                {afterDrawerItems.map((res: any) => (
                  <li key={res.label}>
                    <NavLink
                      to={`/admin/${res.url}`}
                      className={({ isActive }: any) => {
                        const isCurrentlyActive = checkIsActive(isActive, res.url);
                        return `flex items-center p-3 text-base font-medium rounded-xl transition-all duration-200 group ${isCurrentlyActive
                          ? "text-blue-600 bg-blue-50/80 dark:text-blue-400 dark:bg-blue-900/30"
                          : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/50"
                          }`;
                      }}
                    >                <span className="relative inline-flex shrink-0">
                        <res.icon
                          className={`w-6 h-6 ${res.color || 'text-slate-500'} transition duration-75`}
                          aria-hidden="true"
                        />
                        {flaggedSidebarUrls.has(res.url) && <NotificationDot />}
                      </span>
                      <span className="ms-3 tracking-wide" style={{ fontVariantLigatures: "none" }}>{res.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>

              {/* Footer: account menu (avatar dropdown) + identity. Versions now
                  live inside the account dropdown. */}
              <div className="shrink-0 pt-2 border-t border-slate-100 dark:border-slate-700/70">
                <div className="flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <span className="flex shrink-0 items-center">{accountMenu}</span>
                  <div className="min-w-0 flex-1 leading-tight">
                    <p className="truncate text-sm font-semibold capitalize text-slate-800 dark:text-white">
                      {user ? user.displayName : "—"}
                    </p>
                    {user?.designation && (
                      <p className="truncate text-xs font-normal text-slate-500 dark:text-slate-400">
                        {user.designation}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {
          isResettingPassword && (
            <GenericModal
              isOpen={isResettingPassword}
              onClose={() => setIsResettingPassword(false)}
            >
              <ChangePassword onClose={() => setIsResettingPassword(false)}></ChangePassword>
            </GenericModal>
          )
        }

        {isMobile && isOpenmSidebar && (
          <aside
            className={`fixed top-0 left-0 z-40 h-screen pt-16 transition-all bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700 w-24`}
            aria-label="Sidebar"
          >
            <div className="h-full px-3 pb-4 overflow-y-auto">
              <ul className="space-y-2 font-medium">

                {/* MOBILE: Before Drawer Items */}
                {beforeDrawerItems?.map((res: any) => (
                  <li key={res.label}>
                    <NavLink
                      onClick={() => setIsOpenSidebar(false)}
                      to={`/admin/${res.url}`}
                      className={({ isActive }: { isActive: boolean }) => {
                        const isCurrentlyActive = checkIsActive(isActive, res.url);
                        return `flex flex-col items-center p-2 rounded-lg group ${isCurrentlyActive
                          ? "text-blue-600 bg-blue-50/80 dark:text-blue-400 dark:bg-blue-900/30"
                          : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/50"
                          }`;
                      }}
                    >
                      <span className="relative inline-flex">
                        <res.icon className={`w-6 h-6 ${res.color || 'text-slate-500'} transition duration-75`} />
                        {flaggedSidebarUrls.has(res.url) && <NotificationDot />}
                      </span>
                      {isOpenmSidebar && <span className="text-xs text-center mt-1">{res.label}</span>}
                    </NavLink>
                  </li>
                ))}

                {salaryStructureIndex !== -1 && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setIsSalaryDrawerOpen((prev) => !prev)}
                      className={`flex flex-col items-center w-full p-2 rounded-lg group ${isSalaryRouteActive
                        ? "text-blue-600 bg-blue-50/80 dark:text-blue-400 dark:bg-blue-900/30"
                        : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/50"
                        }`}
                    >
                      <Banknote className="w-6 h-6 shrink-0 transition duration-75" aria-hidden="true" />
                      <span className="text-xs text-center mt-1">Salary</span>
                      <span className="mx-auto mt-1">
                        <FaAngleDown
                          className={`transition-transform duration-300 ${isSalaryDrawerOpen ? "rotate-180" : "rotate-0"
                            }`}
                        />
                      </span>
                    </button>
                    <ul
                      className={`pl-1 space-y-1 overflow-hidden transition-all duration-300 ease-in-out ${isSalaryDrawerOpen
                        ? "max-h-[520px] opacity-100 mt-1"
                        : "max-h-0 opacity-0 mt-0 pointer-events-none"
                        }`}
                    >
                      {salaryDrawerItems.map((menuItem) => (
                        <li key={menuItem.url}>
                          <NavLink
                            onClick={() => setIsOpenSidebar(false)}
                            to={`/admin/${menuItem.url}`}
                            className={({ isActive }: { isActive: boolean }) =>
                              `flex items-center justify-center p-2 rounded-lg text-xs ${isActive
                                ? "text-blue-600 bg-blue-50/70 dark:text-blue-400 dark:bg-blue-900/30"
                                : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/50"
                              }`
                            }
                            title={menuItem.label}
                          >
                            <menuItem.icon className="w-5 h-5 shrink-0" aria-hidden="true" />
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </li>
                )}

                {/* MOBILE: After Drawer Items */}
                {afterDrawerItems?.map((res: any) => (
                  <li key={res.label}>
                    <NavLink
                      onClick={() => setIsOpenSidebar(false)}
                      to={`/admin/${res.url}`}
                      className={({ isActive }: { isActive: boolean }) => {
                        const isCurrentlyActive = checkIsActive(isActive, res.url);
                        return `flex flex-col items-center p-2 rounded-lg group ${isCurrentlyActive
                          ? "text-blue-600 bg-blue-50/80 dark:text-blue-400 dark:bg-blue-900/30"
                          : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/50"
                          }`;
                      }}
                    >
                      <span className="relative inline-flex">
                        <res.icon className={`w-6 h-6 ${res.color || 'text-slate-500'} transition duration-75`} />
                        {flaggedSidebarUrls.has(res.url) && <NotificationDot />}
                      </span>
                      {isOpenmSidebar && <span className="text-xs text-center mt-1" style={{ fontVariantLigatures: "none" }}>{res.label}</span>}
                    </NavLink>
                  </li>
                ))}

                <li
                  key={"logout"}
                  className="px-1 mt-2"
                  onClick={() => setLogout(true)}
                >
                  <div className="flex flex-col items-center p-2 text-sm font-semibold text-slate-700 rounded-xl cursor-pointer hover:bg-red-50 hover:text-red-600 group transition-all duration-200 dark:text-slate-300 dark:hover:bg-red-900/20 dark:hover:text-red-400">
                    <LogOut className="w-6 h-6 text-slate-500 group-hover:text-red-600 transition duration-75" />
                    <span className="text-xs text-center mt-1">{t("Logout")}</span>
                  </div>
                </li>
              </ul>

              {/* Version pills — compact for icon-only sidebar; full info on hover tooltip */}
              <div className="mt-4 pt-2 border-t border-slate-100 dark:border-slate-700/70 flex flex-col items-center gap-1">
                <span
                  title={`Web v${APP_VERSION} · Build #${BUILD_NUMBER}`}
                  className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-mono font-medium bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-600/60 cursor-default"
                >
                  #{BUILD_NUMBER}
                </span>
                {/* Server version pill — only shown after api/user/me responds */}
                {serverBuildVersion && (
                  <span
                    title={`Server v${serverBuildVersion}`}
                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-mono font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 border border-blue-100 dark:border-blue-800/50 cursor-default"
                  >
                    S
                  </span>
                )}
              </div>
            </div>
          </aside>
        )}

        <div className={pageColumnClass}>
          {hasPlanBanner && (
            <div className="shrink-0 flex items-center justify-end gap-3 py-1">
              <PlanNotificationBanner
                payload={payload}
                status={subscriptionStatus}
                activeSubdomain={activeSubdomain}
                onVisibilityChange={setIsBannerActive}
              />
            </div>
          )}
          {isUserActive && (
            <div className="flex-1 min-h-0 overflow-auto overscroll-none outline-none">
              {/* Share the already-fetched badge with Outlet pages (e.g. Profile
                  sub-tab indicators) so they don't re-request it. */}
              <NotificationBadgeContext.Provider value={notificationBadge}>
                <BannerContext.Provider value={{ isBannerVisible }}>
                  <Outlet />
                </BannerContext.Provider>
              </NotificationBadgeContext.Provider>
            </div>
          )}
          {!isUserActive && (
            <GenericModal
              isOpen={!isUserActive}
              onClose={() => {
                console.log("logout now :>>>");
              }}
              header="User Info"
            >
              <div className="relative bg-white rounded-lg shadow dark:bg-red-700">
                <div className="p-4 md:p-5 text-center">
                  <svg
                    className="mx-auto mb-4 text-red-400 w-12 h-12 dark:text-red-200"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 20"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                  <h3 className="mb-5 text-lg font-normal text-red-500 dark:text-red-400">
                    <span className="font-medium">Info alert!</span> Your User
                    Id is Removed ,Please Contact Admin.
                  </h3>
                  <button
                    data-modal-hide="popup-modal"
                    type="button"
                    className="text-white bg-red-600 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2 text-center"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </GenericModal>
          )}
          {isChatProfileOpen && (
            <ChatComponent onClose={() => {
              setIsChatProfileOpen(false)
            }} />
          )}
        </div>
      </div>

      {isUserActive && !isOnboarding && (
        <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999, pointerEvents: 'none' }}>
          <Draggable
            position={chatButtonPosition}
            onDrag={handleDrag}
            onStop={handleStop}
            bounds={{ left: 0, top: 0, right: window.innerWidth - 64, bottom: window.innerHeight - 64 }}
          >
            <button
              style={{ pointerEvents: 'auto' }}
              className="inline-flex items-center justify-center text-sm font-medium disabled:pointer-events-none disabled:opacity-50 rounded-full w-16 h-16 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 m-0 cursor-grab active:cursor-grabbing p-0 normal-case leading-5 shadow-lg"
              type="button"
              aria-haspopup="dialog"
              aria-expanded="false"
              data-state="closed"
              onClick={handleChatIconClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="30"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white block border-gray-200 align-middle"
              >
                <path
                  d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"
                  className="border-gray-200"
                ></path>
              </svg>
            </button>
          </Draggable>
        </div>
      )}
    </>
  );
}